import * as Dropdown from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import {
  ReactNode,
  createContext,
  forwardRef,
  memo,
  useCallback,
  useDebugValue,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DefaultQuickActions,
  DefaultQuickActionsContent,
  DefaultToolbarContent,
  DefaultZoomMenuContent,
  MobileStylePanel,
  PORTRAIT_BREAKPOINT,
  TldrawUiButton,
  TldrawUiButtonIcon,
  TldrawUiDropdownMenuContent,
  TldrawUiDropdownMenuRoot,
  TldrawUiDropdownMenuTrigger,
  TldrawUiMenuContextProvider,
  TldrawUiMenuItem,
  unwrapLabel,
  useActions,
  useBreakpoint,
  useContainer,
  useEditor,
  useLocalStorageState,
  useMenuIsOpen,
  usePassThroughWheelEvents,
  useReadonly,
  useTldrawUiComponents,
  useTranslation,
  useUniqueSafeId,
  useValue,
} from "tldraw";

// Modified from https://github.com/tldraw/tldraw/blob/v3.4.0/packages/tldraw/src/lib/ui/components/NavigationPanel/DefaultNavigationPanel.tsx
// Changes: removed breakpoints so that the navigation panel is always visible
const CustomNavigationPanel = memo(function CustomNavigationPanel() {
  const actions = useActions();
  const msg = useTranslation();

  const ref = useRef<HTMLDivElement>(null);
  usePassThroughWheelEvents(ref);

  const [collapsed, setCollapsed] = useLocalStorageState("minimap", true);

  const toggleMinimap = useCallback(() => {
    setCollapsed((s) => !s);
  }, [setCollapsed]);

  const { ZoomMenu, Minimap } = useTldrawUiComponents();

  return (
    <div ref={ref} className="tlui-navigation-panel">
      <div className="tlui-buttons__horizontal">
        {collapsed ? (
          <>
            {ZoomMenu && <ZoomMenu />}
            {Minimap && (
              <TldrawUiButton
                type="icon"
                data-testid="minimap.toggle-button"
                title={msg("navigation-zone.toggle-minimap")}
                className="tlui-navigation-panel__toggle"
                onClick={toggleMinimap}
              >
                <TldrawUiButtonIcon icon={collapsed ? "chevrons-ne" : "chevrons-sw"} />
              </TldrawUiButton>
            )}
          </>
        ) : (
          <>
            <TldrawUiButton
              type="icon"
              data-testid="minimap.zoom-out"
              title={`${msg(unwrapLabel(actions["zoom-out"].label))}`}
              onClick={() => actions["zoom-out"].onSelect("navigation-zone")}
            >
              <TldrawUiButtonIcon icon="minus" />
            </TldrawUiButton>
            {ZoomMenu && <ZoomMenu />}
            <TldrawUiButton
              type="icon"
              data-testid="minimap.zoom-in"
              title={`${msg(unwrapLabel(actions["zoom-in"].label))}`}
              onClick={() => actions["zoom-in"].onSelect("navigation-zone")}
            >
              <TldrawUiButtonIcon icon="plus" />
            </TldrawUiButton>
            {Minimap && (
              <TldrawUiButton
                type="icon"
                data-testid="minimap.toggle-button"
                title={msg("navigation-zone.toggle-minimap")}
                className="tlui-navigation-panel__toggle"
                onClick={toggleMinimap}
              >
                <TldrawUiButtonIcon icon={collapsed ? "chevrons-ne" : "chevrons-sw"} />
              </TldrawUiButton>
            )}
          </>
        )}
      </div>
      {Minimap && !collapsed && <Minimap />}
    </div>
  );
});

function CustomQuickActions() {
  const editor = useEditor();

  const handleClearAll = () => {
    if (confirm("Clear entire whiteboard?")) {
      const allShapeIds = Array.from(editor.getCurrentPageShapeIds());
      editor.deleteShapes(allShapeIds);
    }
  };

  return (
    <DefaultQuickActions>
      {/*
      // @ts-ignore */}
      <DefaultQuickActionsContent />
      <TldrawUiMenuItem id="clear-all" icon="trash" onSelect={handleClearAll} />
    </DefaultQuickActions>
  );
}

// Modified from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/ui/components/Toolbar/DefaultToolbar.tsx
// Changes: uses CustomOverflowingToolbar instead of OverflowingToolbar
// NOTE: this can all be removed if/when tldraw makes the number shortcuts optional (https://discord.com/channels/859816885297741824/1308559134626676847/1308740487787712533)
export const CustomToolbar = memo(function DefaultToolbar({ children }: { children?: ReactNode }) {
  const editor = useEditor();
  const breakpoint = useBreakpoint();
  const isReadonlyMode = useReadonly();
  const activeToolId = useValue("current tool id", () => editor.getCurrentToolId(), [editor]);

  const ref = useRef<HTMLDivElement>(null);
  usePassThroughWheelEvents(ref);

  const { ActionsMenu, QuickActions } = useTldrawUiComponents();

  const showQuickActions =
    editor.options.actionShortcutsLocation === "menu"
      ? false
      : editor.options.actionShortcutsLocation === "toolbar"
        ? true
        : breakpoint < PORTRAIT_BREAKPOINT.TABLET;

  return (
    <div ref={ref} className="tlui-toolbar">
      <div className="tlui-toolbar__inner">
        <div className="tlui-toolbar__left">
          {!isReadonlyMode && (
            <div className="tlui-toolbar__extras">
              {showQuickActions && (
                <div className="tlui-toolbar__extras__controls tlui-buttons__horizontal">
                  {QuickActions && <QuickActions />}
                  {ActionsMenu && <ActionsMenu />}
                </div>
              )}
              <ToggleToolLockedButton activeToolId={activeToolId} />
            </div>
          )}
          <CustomOverflowingToolbar>
            {children ?? <DefaultToolbarContent />}
          </CustomOverflowingToolbar>
        </div>
        {breakpoint < PORTRAIT_BREAKPOINT.TABLET_SM && !isReadonlyMode && (
          <div className="tlui-toolbar__tools">
            <MobileStylePanel />
          </div>
        )}
      </div>
    </div>
  );
});

// Modified from https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/ui/components/Toolbar/OverflowingToolbar.tsx
// Changes: removes number shortcuts for changing tools
export const IsInOverflowContext = createContext(false);

export function CustomOverflowingToolbar({ children }: { children: ReactNode }) {
  const id = useUniqueSafeId();
  const breakpoint = useBreakpoint();
  const msg = useTranslation();

  // We also change one of the values here from 8 to 5 in order to show fewer tools in the toolbar
  const overflowIndex = Math.min(5, 5 + breakpoint);

  const [totalItems, setTotalItems] = useState(0);
  const mainToolsRef = useRef<HTMLDivElement>(null);
  const [lastActiveOverflowItem, setLastActiveOverflowItem] = useState<string | null>(null);

  const css = useMemo(() => {
    const activeCss = lastActiveOverflowItem
      ? `:not([data-value="${lastActiveOverflowItem}"])`
      : "";

    return `
			#${id}_main > *:nth-child(n + ${overflowIndex + (lastActiveOverflowItem ? 1 : 2)})${activeCss} {
				display: none;
			}
			#${id}_more > *:nth-child(-n + ${overflowIndex}) {
				display: none;
			}
        `;
  }, [lastActiveOverflowItem, id, overflowIndex]);

  const onDomUpdate = useEvent(() => {
    if (!mainToolsRef.current) return;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const children = Array.from(mainToolsRef.current.children);
    setTotalItems(children.length);

    // If the last active overflow item is no longer in the overflow, clear it
    const lastActiveElementIdx = children.findIndex(
      (el) => el.getAttribute("data-value") === lastActiveOverflowItem,
    );
    if (lastActiveElementIdx <= overflowIndex) {
      setLastActiveOverflowItem(null);
    }

    // But if there's a new active item...
    const activeElementIdx = Array.from(mainToolsRef.current.children).findIndex(
      (el) => el.getAttribute("aria-checked") === "true",
    );
    if (activeElementIdx === -1) return;

    // ...and it's in the overflow, set it as the last active overflow item
    if (activeElementIdx >= overflowIndex) {
      setLastActiveOverflowItem(children[activeElementIdx].getAttribute("data-value"));
    }
  });

  useLayoutEffect(() => {
    onDomUpdate();
  });

  useLayoutEffect(() => {
    if (!mainToolsRef.current) return;

    const mutationObserver = new MutationObserver(onDomUpdate);
    mutationObserver.observe(mainToolsRef.current, {
      childList: true,
      subtree: true,
      attributeFilter: ["data-value", "aria-checked"],
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [onDomUpdate]);

  return (
    <>
      <style>{css}</style>
      <div
        className={clsx("tlui-toolbar__tools", {
          "tlui-toolbar__tools__mobile": breakpoint < PORTRAIT_BREAKPOINT.TABLET_SM,
        })}
        role="radiogroup"
      >
        <div id={`${id}_main`} ref={mainToolsRef} className="tlui-toolbar__tools__list">
          <TldrawUiMenuContextProvider type="toolbar" sourceId="toolbar">
            {children}
          </TldrawUiMenuContextProvider>
        </div>
        {/* There is a +1 because if the menu is just one item, it's not necessary. */}
        {totalItems > overflowIndex + 1 && (
          <IsInOverflowContext.Provider value>
            <TldrawUiDropdownMenuRoot id="toolbar overflow" modal={false}>
              <TldrawUiDropdownMenuTrigger>
                <TldrawUiButton
                  title={msg("tool-panel.more")}
                  type="tool"
                  className="tlui-toolbar__overflow"
                  data-testid="tools.more-button"
                >
                  <TldrawUiButtonIcon icon="chevron-up" />
                </TldrawUiButton>
              </TldrawUiDropdownMenuTrigger>
              <TldrawUiDropdownMenuContent side="top" align="center">
                <div
                  className="tlui-buttons__grid"
                  data-testid="tools.more-content"
                  id={`${id}_more`}
                >
                  <TldrawUiMenuContextProvider type="toolbar-overflow" sourceId="toolbar">
                    {children}
                  </TldrawUiMenuContextProvider>
                </div>
              </TldrawUiDropdownMenuContent>
            </TldrawUiDropdownMenuRoot>
          </IsInOverflowContext.Provider>
        )}
      </div>
    </>
  );
}

// Directly copied from https://github.com/tldraw/tldraw/blob/v3.4.0/packages/tldraw/src/lib/ui/components/Toolbar/ToggleToolLockedButton.tsx
// No changes: only needed because we cannot import it
function ToggleToolLockedButton({ activeToolId }: { activeToolId?: string }) {
  const editor = useEditor();
  const breakpoint = useBreakpoint();
  const msg = useTranslation();

  const isToolLocked = useValue("is tool locked", () => editor.getInstanceState().isToolLocked, [
    editor,
  ]);
  const tool = useValue("current tool", () => editor.getCurrentTool(), [editor]);

  if (!activeToolId || !tool.isLockable) return null;

  return (
    <TldrawUiButton
      type="normal"
      title={msg("action.toggle-tool-lock")}
      data-testid="tool-lock"
      className={clsx("tlui-toolbar__lock-button", {
        "tlui-toolbar__lock-button__mobile": breakpoint < PORTRAIT_BREAKPOINT.TABLET_SM,
      })}
      onClick={() => editor.updateInstanceState({ isToolLocked: !isToolLocked })}
    >
      <TldrawUiButtonIcon icon={isToolLocked ? "lock" : "unlock"} small />
    </TldrawUiButton>
  );
}

// Directly copied from https://github.com/tldraw/tldraw/blob/v3.4.0/packages/editor/src/lib/hooks/useEvent.tsx
// No changes: only needed because we cannot import it
function useEvent<Args extends Array<unknown>, Result>(
  handler: (...args: Args) => Result,
): (...args: Args) => Result {
  const handlerRef = useRef<(...args: Args) => Result>();

  // In a real implementation, this would run before layout effects
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useDebugValue(handler);

  return useCallback((...args: Args) => {
    // In a real implementation, this would throw if called during render
    const fn = handlerRef.current;
    if (!fn) {
      throw new Error("fn does not exist");
    }
    return fn(...args);
  }, []);
}

// Modified from https://github.com/tldraw/tldraw/blob/v3.4.0/packages/tldraw/src/lib/ui/components/ZoomMenu/DefaultZoomMenu.tsx
// Changes: menu opens from below instead of above
// NOTE: this requires @radix-ui/react-dropdown-menu v2.1.1 (there is a breaking change with 2.1.2)
const CustomZoomMenu = memo(function CustomZoomMenu() {
  const container = useContainer();
  const [isOpen, onOpenChange] = useMenuIsOpen("zoom menu");

  return (
    <Dropdown.Root dir="ltr" open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <Dropdown.Trigger asChild dir="ltr">
        <CustomZoomTriggerButton />
      </Dropdown.Trigger>
      <Dropdown.Portal container={container}>
        <Dropdown.Content
          className="tlui-menu"
          side="bottom"
          align="start"
          alignOffset={0}
          sideOffset={8}
          collisionPadding={4}
        >
          <TldrawUiMenuContextProvider type="menu" sourceId="zoom-menu">
            <DefaultZoomMenuContent />
          </TldrawUiMenuContextProvider>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
});

// Modified from https://github.com/tldraw/tldraw/blob/v3.4.0/packages/tldraw/src/lib/ui/components/ZoomMenu/DefaultZoomMenu.tsx
// Changes: removed breakpoints so that the text is always visible
const CustomZoomTriggerButton = forwardRef<HTMLButtonElement, any>(
  function CustomZoomTriggerButton(props, ref) {
    const editor = useEditor();
    const zoom = useValue("zoom", () => editor.getZoomLevel(), [editor]);
    const msg = useTranslation();

    return (
      <TldrawUiButton
        ref={ref}
        {...props}
        type="icon"
        title={`${msg("navigation-zone.zoom")}`}
        data-testid="minimap.zoom-menu-button"
        className="tlui-zoom-menu__button__pct"
      >
        <span style={{ flexGrow: 0, textAlign: "center" }}>{Math.floor(zoom * 100)}%</span>
      </TldrawUiButton>
    );
  },
);

export { CustomNavigationPanel, CustomQuickActions, CustomZoomMenu };
